import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { bannerText } from './bannerText';

// Set only in Vercel's Preview scope (e.g. "DEV"); scripts/check-build-env.js fails a Production build that sets it.
const envLabel = process.env.REACT_APP_ENV_LABEL;

/**
 * Wraps the whole app. Without REACT_APP_ENV_LABEL it renders the app untouched. With it, a strip naming the
 * environment and its API sits in normal flow above the app, which gets the rest of the viewport as its positioned
 * scroll container, so pages that fill the screen with position: absolute stay below the strip. A PV build also
 * names its frontend pull request (#148).
 */
export default function EnvironmentBanner({ children }: { children: React.ReactElement }) {
  if (!envLabel) {
    return children;
  }
  const text = bannerText({
    label: envLabel,
    apiUrl: process.env.REACT_APP_API_URL ?? '',
    pvPr: process.env.REACT_APP_PV_PR,
    pvTitle: process.env.REACT_APP_PV_TITLE,
  });
  return (
    <Box sx={{ position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        component="aside"
        aria-label="Environment"
        title={text.tooltip}
        sx={{
          flex: 'none',
          // A long API host must wrap inside the strip, not widen it past the screen.
          minWidth: 0,
          display: 'flex',
          // One line on a wide screen. Below sm, the pull request is the first line and the backend is the second.
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: 'wrap',
          alignItems: { xs: 'stretch', sm: 'center' },
          justifyContent: 'center',
          columnGap: 1,
          rowGap: 0,
          width: '100%',
          boxSizing: 'border-box',
          px: 2,
          py: 0.5,
          bgcolor: 'warning.light',
          color: 'common.black',
          textAlign: 'center',
        }}
      >
        <Typography
          variant="body2"
          component="p"
          sx={{ m: 0, maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere' }}
        >
          <Box component="span" sx={{ fontWeight: 700 }}>
            {text.lead}
          </Box>
          {text.description ? ` (${text.description})` : null}
        </Typography>
        <Typography
          variant="body2"
          component="p"
          sx={{ m: 0, maxWidth: '100%', minWidth: 0, overflowWrap: 'anywhere' }}
        >
          {text.api}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative', flex: 1, minHeight: 0, overflow: 'auto' }}>{children}</Box>
    </Box>
  );
}
